import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  AdminRow,
  AdminUserRow,
  BrandRow,
  DayDetail,
  KeywordBatchView,
  KeywordDashboard,
  MailAccountView,
  MailboxRow,
  Paginated,
  ProviderPreset,
  ProvisionedCredentials,
  SuperOverview,
  SuperUserDetail,
  SuperUserRow,
} from '@nlinks/shared';
import { api } from './api';

/**
 * Query key factory. Centralising the keys is what makes targeted invalidation
 * possible — e.g. `queryKeys.dashboard` invalidates every dashboard panel
 * without touching the mailbox list.
 */
export const queryKeys = {
  mailAccounts: ['mail-accounts'] as const,
  providers: ['providers'] as const,
  keywords: ['keywords'] as const,
  adminUsers: (params: Record<string, unknown>) => ['admin', 'users', params] as const,
};

function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ── mailboxes ────────────────────────────────────────────────────────────────

export const useMailAccounts = (options?: Partial<UseQueryOptions<MailAccountView[]>>) =>
  useQuery({
    queryKey: queryKeys.mailAccounts,
    queryFn: () => api.get<MailAccountView[]>('/api/mail-accounts'),
    ...options,
  });

export const useProviderPresets = () =>
  useQuery({
    queryKey: queryKeys.providers,
    queryFn: () => api.get<ProviderPreset[]>('/api/mail-accounts/providers'),
    // Static reference data — never goes stale within a session.
    staleTime: Infinity,
  });

export function useMailAccountMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.mailAccounts });
    void qc.invalidateQueries({ queryKey: queryKeys.keywords });
  };

  return {
    create: useMutation({
      mutationFn: (body: unknown) => api.post<MailAccountView>('/api/mail-accounts', body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: unknown }) =>
        api.patch<MailAccountView>(`/api/mail-accounts/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/api/mail-accounts/${id}`),
      onSuccess: invalidate,
    }),
    sync: useMutation({
      mutationFn: (id: string) => api.post(`/api/mail-accounts/${id}/sync`),
      onSuccess: invalidate,
    }),
    syncAll: useMutation({
      mutationFn: () => api.post<{ queued: number }>('/api/mail-accounts/sync-all'),
      onSuccess: invalidate,
    }),
  };
}

// ── admin ────────────────────────────────────────────────────────────────────

export const useAdminUsers = (params: Record<string, unknown>) =>
  useQuery({
    queryKey: queryKeys.adminUsers(params),
    queryFn: () => api.get<Paginated<AdminUserRow>>(`/api/admin/users${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

// ── keyword batches ──────────────────────────────────────────────────────────

export const useKeywordDashboard = (batchId?: string) =>
  useQuery({
    queryKey: ['keywords', 'dashboard', batchId ?? 'all'],
    queryFn: () =>
      api.get<KeywordDashboard>(`/api/keywords/dashboard${batchId ? `?batchId=${batchId}` : ''}`),
    // The SSE stream pushes updates; this only covers a dropped connection.
    refetchInterval: 60_000,
  });

/** Everything that arrived on one UTC day, matched or not. */
export const useDayDetail = (day: string | undefined) =>
  useQuery({
    queryKey: ['day', day],
    queryFn: () => api.get<DayDetail>(`/api/days/${day}`),
    enabled: Boolean(day),
  });

export const useKeywordBatches = () =>
  useQuery({ queryKey: ['keywords', 'list'], queryFn: () => api.get<KeywordBatchView[]>('/api/keywords') });

export function useKeywordBatchMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.keywords });
    void qc.invalidateQueries({ queryKey: queryKeys.mailAccounts });
  };

  return {
    save: useMutation({
      mutationFn: (body: { raw: string; deactivateExisting: boolean }) =>
        api.post<{ id: string; keywords: string[]; mailboxesQueued: number }>('/api/keywords', body),
      onSuccess: invalidate,
    }),
    setActive: useMutation({
      mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
        api.patch<{ isActive: boolean }>(`/api/keywords/${id}`, { isActive }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<{ untaggedMessages: number }>(`/api/keywords/${id}`),
      onSuccess: invalidate,
    }),
    preview: useMutation({
      mutationFn: (raw: string) => api.post<{ keywords: string[] }>('/api/keywords/preview', { raw }),
    }),
  };
}

// ── super admin ──────────────────────────────────────────────────────────────

export const useSuperOverview = () =>
  useQuery({ queryKey: ['super', 'overview'], queryFn: () => api.get<SuperOverview>('/api/super/overview'), refetchInterval: 30_000 });

export const useSuperUsers = (params: Record<string, unknown>) =>
  useQuery({
    queryKey: ['super', 'users', params],
    queryFn: () => api.get<Paginated<SuperUserRow>>(`/api/super/users${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

export const useSuperUser = (id: string | null, revealSecrets: boolean) =>
  useQuery({
    queryKey: ['super', 'user', id, revealSecrets],
    queryFn: () => api.get<SuperUserDetail>(`/api/super/users/${id}?revealSecrets=${revealSecrets}`),
    enabled: Boolean(id),
  });

export const useAdmins = () =>
  useQuery({ queryKey: ['super', 'admins'], queryFn: () => api.get<AdminRow[]>('/api/super/admins') });

export const useAllMailboxes = (params: Record<string, unknown>) =>
  useQuery({
    queryKey: ['super', 'mailboxes', params],
    queryFn: () => api.get<Paginated<MailboxRow>>(`/api/super/mailboxes${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

export const useBrands = (limit = 25) =>
  useQuery({ queryKey: ['super', 'brands', limit], queryFn: () => api.get<BrandRow[]>(`/api/super/brands?pageSize=${limit}`) });

export function useSuperMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['super'] });
  return {
    createAdmin: useMutation({
      mutationFn: (body: { fullName: string; email: string }) =>
        api.post<{ admin: { email: string; fullName: string }; credentials: ProvisionedCredentials }>('/api/super/admins', body),
      onSuccess: invalidate,
    }),
    deleteAdmin: useMutation({ mutationFn: (id: string) => api.delete(`/api/super/admins/${id}`), onSuccess: invalidate }),
    /**
     * Reuses the admin endpoint rather than adding a super-admin twin: holding
     * a user is the same action whoever performs it, and `requireAdmin` already
     * admits a super admin.
     */
    toggleHold: useMutation({
      mutationFn: (id: string) => api.post<{ status: string }>(`/api/admin/users/${id}/hold`),
      onSuccess: invalidate,
    }),
    deleteUser: useMutation({ mutationFn: (id: string) => api.delete(`/api/super/users/${id}`), onSuccess: invalidate }),
    resetPassword: useMutation({
      mutationFn: (id: string) => api.post<ProvisionedCredentials>(`/api/super/users/${id}/reset-password`),
      onSuccess: invalidate,
    }),
  };
}

// ── admin (simplified) ───────────────────────────────────────────────────────

export function useAdminActions() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin'] });
  return {
    createUser: useMutation({
      mutationFn: (body: { fullName: string; email: string }) =>
        api.post<{ user: { email: string; fullName: string }; credentials: ProvisionedCredentials }>('/api/admin/users', body),
      onSuccess: invalidate,
    }),
    toggleHold: useMutation({
      mutationFn: (id: string) => api.post<{ status: string }>(`/api/admin/users/${id}/hold`),
      onSuccess: invalidate,
    }),
    deleteUser: useMutation({ mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`), onSuccess: invalidate }),
  };
}
